// Entry Cuti — input cuti oleh ADMIN DIVISI.
//
// Alurnya sama dengan "Tambah Data" di menu Cuti HR (/human-resource/transaksi/cuti):
// simpan → Belum Dikirim → Kirim → approval. Bedanya cakupan: karyawan & pengganti yang
// bisa dipilih hanya yang SATU Bagian dengan user yang login. Bagian itu diturunkan
// backend dari token (endpoint /entryCuti/*), bukan dari localStorage — snapshot login
// di localStorage tak pernah disegarkan, dan backend menolak Nik di luar Bagian.

import Button from "@material-tailwind/react/Button";
import Modal from "@material-tailwind/react/Modal";
import ModalBody from "@material-tailwind/react/ModalBody";
import ModalFooter from "@material-tailwind/react/ModalFooter";
import ModalHeader from "@material-tailwind/react/ModalHeader";
import { useMutation } from "@tanstack/react-query";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import Select from "react-select";
import { toast } from "react-toastify";
import { Colors } from "../../../../assets/Colors";
import { maxPageGlobal } from "../../../../assets/config";
import { HrdInputGenerator } from "../../../../components/form/HrdInputGenerator";
import { isRequired, useForm } from "../../../../components/isRequired";
import { fetchPostSilance } from "../../../../fetchers";
import CutiApprovalActors from "./CutiApprovalActors";
import { getMinCuti } from "../../../master/cuti/masterCutiSlice";
import { tanggalLokal } from "./tanggalLokal";
import FilterTahunRiwayat, { pesanKosong, saringTahun, tahunIni } from "./FilterTahunRiwayat";

const TOAST = {
  position: "bottom-right", autoClose: 5000, hideProgressBar: false,
  closeOnClick: true, pauseOnHover: false, draggable: true, theme: "light",
};
const pesanError = (e) => e?.response?.data?.message || e?.message || "Terjadi kesalahan";

const LABEL = "block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2";
// Menu react-select dirender ke document.body (agar tak terpotong overflow modal), jadi
// z-index-nya harus di atas Modal material-tailwind (z-50) — tanpa ini daftar pilihan
// muncul DI BELAKANG modal dan karyawan tak bisa dipilih.
const SELECT_DI_MODAL = { menuPortal: (base) => ({ ...base, zIndex: 9999 }) };
const READONLY =
  "appearance-none block w-full text-gray-700 border border-gray-200 rounded py-2.5 px-4 leading-tight outline-none bg-gray-50";

const EntryCutiPage = () => {
  const dispatch = useDispatch();

  const initialState = {
    Nik: "",
    JenisCuti: -1,
    KeperluanCuti: "",
    Pengganti: "",
    Tiket: false,
    TanggalMulai: new Date(),
    TanggalAkhir: new Date(),
  };
  const validations = [
    ({ Nik }) => isRequired(Nik) || { Nik: "Karyawan wajib diisi" },
    ({ JenisCuti }) => isRequired(JenisCuti) || { JenisCuti: "Jenis cuti wajib diisi" },
    ({ KeperluanCuti }) => isRequired(KeperluanCuti) || { KeperluanCuti: "Keperluan cuti wajib diisi" },
    ({ Pengganti }) => isRequired(Pengganti) || { Pengganti: "Pengganti wajib diisi" },
  ];
  const {
    values, isValid, errors, touched, changed,
    clearHandler, customHandler, changeHandler, multiFieldHandler,
  } = useForm(initialState, validations);
  const [buttonTouched, setButtonTouched] = useState(false);

  const [page, setPage] = useState(1);
  const [maxPage, setMaxPage] = useState(0);
  const [search, setSearch] = useState("");
  const [param, setParam] = useState({ start: 0, count: maxPageGlobal, search: "" });
  const count = maxPageGlobal;

  const [showModalAdd, setShowModalAdd] = useState(false);
  const [showModalDetail, setShowModalDetail] = useState(false);
  const [selectedKaryawan, setSelectedKaryawan] = useState(null);
  const [tahunRiwayat, setTahunRiwayat] = useState(tahunIni());
  const [jumlahHari, setJumlahHari] = useState(0);
  const [saldoKurang, setSaldoKurang] = useState(true);
  // Sisa hak cuti PERIODE tanggal mulai: kontrak PKWT yang mencakupnya, selain itu tahun kalender.
  const [saldoTahun, setSaldoTahun] = useState(null);

  const mutationKaryawan = useMutation(() => fetchPostSilance("entryCuti/karyawan", new FormData()));
  const mutationGrouped = useMutation((p) => fetchPostSilance("entryCuti/grouped", p));
  const mutationDetail = useMutation((p) => fetchPostSilance("entryCuti/byNik", p));
  const mutationInput = useMutation((p) => fetchPostSilance("entryCuti/insert", p));
  const mutationKirim = useMutation((p) => fetchPostSilance("entryCuti/kirim", p));
  const mutationHari = useMutation((p) => fetchPostSilance("calculateCuti", p));

  // Daftar karyawan Bagian user — sekaligus sumber pilihan Pengganti.
  useEffect(() => { mutationKaryawan.mutate(); }, []); // eslint-disable-line
  const karyawanList = useMemo(() => mutationKaryawan.data?.data || [], [mutationKaryawan.data]);
  const namaBagian = karyawanList[0]?.NamaBagian || "";
  // Backend membalas 403 + pesan bila akun tak terhubung ke data karyawan (Bagian tak diketahui).
  const pesanAkses = mutationKaryawan.isError ? pesanError(mutationKaryawan.error) : "";

  const terpilih = useMemo(
    () => karyawanList.find((k) => k.value === values.Nik) || null,
    [karyawanList, values.Nik]
  );
  const opsiPengganti = useMemo(
    () => karyawanList.filter((k) => k.value !== values.Nik),
    [karyawanList, values.Nik]
  );

  useEffect(() => { loadGrouped(); }, [param]); // eslint-disable-line
  function loadGrouped() {
    const fd = new FormData();
    fd.append("start", param.start.toString());
    fd.append("count", param.count.toString());
    fd.append("search", param.search.toString());
    mutationGrouped.mutate(fd, {
      onSuccess: (data) => {
        const total = data?.count || 0;
        setMaxPage(total > 0 ? Math.ceil(total / count) : 1);
      },
    });
  }

  // Jenis cuti yang berlaku tergantung Golongan & Tipe karyawan yang dipilih.
  useEffect(() => {
    if (terpilih) {
      dispatch(getMinCuti({ GolonganID: terpilih.GolonganID, TipeKaryawanID: terpilih.TipeKaryawanID }));
    }
  }, [terpilih?.value]); // eslint-disable-line

  // Hitung hari terpakai & bandingkan dengan saldo (logika sama dengan menu Cuti HR).
  useEffect(() => {
    if (values.Nik && values.TanggalMulai && values.TanggalAkhir && values.JenisCuti !== -1) {
      mutationHari.mutate(
        {
          TanggalMulai: tanggalLokal(values.TanggalMulai),
          TanggalAkhir: tanggalLokal(values.TanggalAkhir),
          CutiID: values.JenisCuti.toString(),
          Nik: values.Nik,
        },
        {
          onSuccess: (data) => {
            const range = parseInt(data.data.Result);
            const saldo = data.data.Saldo ?? parseInt(terpilih?.SisahCuti || 0);
            setJumlahHari(range);
            setSaldoTahun(data.data.Saldo != null ? {
                tahun: data.data.Tahun, saldo: data.data.Saldo, periode: data.data.Periode,
                peringatan: data.data.Peringatan, blokir: data.data.Blokir,
              } : null);
            setSaldoKurang(saldo < range || !!data.data.Blokir);
          },
        }
      );
    }
  }, [values.Nik, values.JenisCuti, values.TanggalMulai, values.TanggalAkhir]); // eslint-disable-line

  function openDetail(row) {
    setSelectedKaryawan(row);
    setTahunRiwayat(tahunIni());
    const fd = new FormData();
    fd.append("Nik", row.Nik);
    mutationDetail.mutate(fd);
    setShowModalDetail(true);
  }

  function kirim(item) {
    mutationKirim.mutate({ TCutiID: item.TCutiID }, {
      onSuccess: () => {
        const fd = new FormData();
        fd.append("Nik", selectedKaryawan.Nik);
        mutationDetail.mutate(fd);
        loadGrouped();
        toast.success("Berhasil mengirim cuti", TOAST);
      },
      onError: (e) => toast.error(pesanError(e), TOAST),
    });
  }

  function tutupForm() {
    setShowModalAdd(false);
    setButtonTouched(false);
    setJumlahHari(0);
    setSaldoKurang(true);
    setSaldoTahun(null);
    clearHandler();
  }

  function simpan() {
    if (!isValid) {
      if (changed) setButtonTouched(true);
      return;
    }
    const fd = new FormData();
    fd.append("Nik", values.Nik);
    fd.append("JenisCuti", values.JenisCuti);
    fd.append("KeperluanCuti", values.KeperluanCuti);
    fd.append("Pengganti", values.Pengganti);
    fd.append("TanggalMulai", tanggalLokal(values.TanggalMulai));
    fd.append("TanggalAkhir", tanggalLokal(values.TanggalAkhir));
    fd.append("Tiket", values.Tiket);
    mutationInput.mutate(fd, {
      onSuccess: (data) => {
        toast.success(data.message || "Cuti tersimpan — klik Detail lalu Kirim untuk mengajukan", TOAST);
        tutupForm();
        setPage(1);
        setParam({ start: 0, count, search: "" });
        setSearch("");
      },
      onError: (e) => toast.error(pesanError(e), TOAST),
    });
  }

  function handleSearch(e) {
    setSearch(e.target.value);
    setPage(1);
    setParam({ start: 0, count, search: e.target.value });
  }
  function handlePage(p) {
    setPage(p);
    setParam({ start: p * count - count, count, search });
  }

  const formLain = [
    { type: "cuti", name: "JenisCuti", title: "Jenis Cuti", handler: customHandler },
    { type: "text", name: "KeperluanCuti", title: "Keperluan Cuti", handler: changeHandler },
    { type: "dateOnly", name: "TanggalMulai", title: "Tanggal Mulai", handler: customHandler },
    { type: "dateEnd", name: "TanggalAkhir", title: "Tanggal Akhir", handler: customHandler },
    { type: "yesNot", name: "Tiket", title: "Tiket", handler: customHandler },
  ];
  const errTampil = (name) => (touched[name] || buttonTouched) && errors[name];

  const rows = mutationGrouped.data?.data || [];
  const riwayatSemua = mutationDetail.data?.data || [];
  const riwayatTampil = saringTahun(riwayatSemua, tahunRiwayat);
  const bisaSimpan = isValid && !saldoKurang && !mutationInput.isLoading;

  return (
    <>
      <div className="md:ml-64">
        <div style={{ background: Colors.primary }} className="pt-14 pb-16 px-3 md:px-8 h-auto">
          <div className="container mx-auto max-w-full" />
        </div>

        <div className="px-3 md:px-8 h-auto -mt-32">
          <div className="container mx-auto max-w-full">
            <div className="grid grid-cols-1 px-4">
              <div className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="flex flex-wrap items-center justify-between px-6 py-4 border-b gap-2">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-700">Entry Cuti</h2>
                    <p className="text-xs text-gray-500">
                      {namaBagian
                        ? <>Karyawan Bagian <strong>{namaBagian}</strong></>
                        : "Input cuti karyawan satu Bagian dengan Anda"}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <input
                      type="text"
                      placeholder="Cari nama karyawan..."
                      value={search}
                      onChange={handleSearch}
                      className="border border-gray-300 rounded px-3 py-1.5 text-sm outline-none focus:border-blue-400"
                    />
                    <Button
                      color="green" buttonType="solid" size="sm"
                      disabled={!!pesanAkses || karyawanList.length === 0}
                      onClick={() => { clearHandler(); setShowModalAdd(true); }}
                    >
                      <i className="material-icons mr-1" style={{ fontSize: 16 }}>add</i>
                      Entry Cuti
                    </Button>
                  </div>
                </div>

                {pesanAkses && (
                  <div className="mx-6 mt-4 px-4 py-2.5 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
                    {pesanAkses}
                  </div>
                )}

                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
                      <tr>
                        <th className="px-4 py-3">No</th>
                        <th className="px-4 py-3">Nama Karyawan</th>
                        <th className="px-4 py-3 text-center">Saldo Hak Cuti</th>
                        <th className="px-4 py-3 text-center">Jumlah Cuti Diambil</th>
                        <th className="px-4 py-3 text-center">Saldo Akhir Cuti</th>
                        <th className="px-4 py-3 text-center">Aksi</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {mutationGrouped.isLoading ? (
                        <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">Loading...</td></tr>
                      ) : rows.length === 0 ? (
                        <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">
                          Belum ada cuti tahun ini untuk karyawan Bagian Anda
                        </td></tr>
                      ) : (
                        rows.map((row, idx) => (
                          <tr key={row.Nik} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-gray-500">{param.start + idx + 1}</td>
                            <td className="px-4 py-3 font-medium text-gray-800">
                              {row.NamaKaryawan}
                              {row.Periode && <div className="text-xs font-normal text-gray-400">{row.Periode}</div>}
                            </td>
                            <td className="px-4 py-3 text-center">{row.HakCuti}</td>
                            <td className="px-4 py-3 text-center">{row.JmlCutiDiambil}</td>
                            <td className="px-4 py-3 text-center">
                              <span className={parseInt(row.SaldoAkhirCuti) <= 3 ? "text-red-600 font-semibold" : "text-green-600 font-semibold"}>
                                {row.SaldoAkhirCuti}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <button onClick={() => openDetail(row)}
                                className="text-xs bg-blue-500 hover:bg-blue-600 text-white px-3 py-1.5 rounded">
                                Detail
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {maxPage > 1 && (
                  <div className="flex items-center justify-end gap-2 px-6 py-3 border-t">
                    <button disabled={page <= 1} onClick={() => handlePage(page - 1)}
                      className="px-3 py-1 text-sm border rounded disabled:opacity-40 hover:bg-gray-100">&laquo;</button>
                    <span className="text-sm text-gray-600">Halaman {page} / {maxPage}</span>
                    <button disabled={page >= maxPage} onClick={() => handlePage(page + 1)}
                      className="px-3 py-1 text-sm border rounded disabled:opacity-40 hover:bg-gray-100">&raquo;</button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Riwayat cuti per karyawan + tombol Kirim */}
      <Modal size="xl" active={showModalDetail} toggler={() => setShowModalDetail(false)}>
        <ModalHeader toggler={() => setShowModalDetail(false)}>
          Riwayat Cuti — {selectedKaryawan?.NamaKaryawan}
        </ModalHeader>
        <ModalBody>
          <FilterTahunRiwayat rows={riwayatSemua} value={tahunRiwayat} onChange={setTahunRiwayat} />
          {mutationDetail.isLoading ? (
            <p className="text-center py-4 text-gray-400">Loading...</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
                  <tr>
                    <th className="px-3 py-2">ID</th>
                    <th className="px-3 py-2">Tanggal</th>
                    <th className="px-3 py-2 text-center">Saldo Awal</th>
                    <th className="px-3 py-2 text-center">Diambil</th>
                    <th className="px-3 py-2 text-center">Saldo Akhir</th>
                    <th className="px-3 py-2">Keterangan</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">Approval</th>
                    <th className="px-3 py-2 text-center">Aksi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {riwayatTampil.length === 0 ? (
                    <tr><td colSpan={9} className="px-3 py-6 text-center text-gray-400">{pesanKosong(tahunRiwayat)}</td></tr>
                  ) : (
                    riwayatTampil.map((item) => (
                      <tr key={item.TCutiID} className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-gray-500">{item.TCutiID}</td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {item.TanggalMonitoring}
                          {item.Periode && <div className="text-xs text-gray-400">{item.Periode}</div>}
                        </td>
                        <td className="px-3 py-2 text-center">{item.HakCuti}</td>
                        <td className="px-3 py-2 text-center">{item.JmlCutiDiambil}</td>
                        <td className="px-3 py-2 text-center">
                          <span className={parseInt(item.SaldoAkhirCuti) <= 3 ? "text-red-600 font-semibold" : "text-green-600 font-semibold"}>
                            {item.SaldoAkhirCuti}
                          </span>
                        </td>
                        <td className="px-3 py-2">{item.JenisCutiKet} — {item.KeperluanCuti}</td>
                        <td className="px-3 py-2">
                          <span className={item.StatusKet === "Selesai" ? "text-green-600" : item.StatusKet === "Ditolak" ? "text-red-600" : "text-yellow-600"}>
                            {item.StatusKet}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-xs align-top">
                          <CutiApprovalActors list={item.ApprovalList} />
                        </td>
                        <td className="px-3 py-2 text-center">
                          {item.StatusKet === "Belum Dikirim" && (
                            <button onClick={() => kirim(item)} disabled={mutationKirim.isLoading}
                              className="text-xs bg-blue-500 hover:bg-blue-600 text-white px-3 py-1.5 rounded whitespace-nowrap disabled:opacity-50">
                              Kirim
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="blueGray" buttonType="link" onClick={() => setShowModalDetail(false)}>Tutup</Button>
        </ModalFooter>
      </Modal>

      {/* Form Entry Cuti */}
      <Modal size="lg" active={showModalAdd} toggler={tutupForm}>
        <ModalHeader className="mt-20" toggler={tutupForm}>Entry Cuti</ModalHeader>
        <ModalBody className="w-full">
          <form className="w-96" onSubmit={(e) => e.preventDefault()}>
            {namaBagian && (
              <div className="mb-4 px-3 py-2 rounded bg-blue-50 border border-blue-100 text-xs text-blue-700">
                Hanya karyawan Bagian <strong>{namaBagian}</strong> yang bisa dipilih.
              </div>
            )}

            {/* Karyawan — dibatasi Bagian user */}
            <div className="flex flex-wrap -mx-3 mb-6">
              <div className="w-full md:w-1/2 px-3 mb-6 md:mb-0">
                <label className={LABEL}>Karyawan <span className="text-red-500">*</span></label>
                <Select
                  menuPortalTarget={document.body}
                  styles={SELECT_DI_MODAL}
                  value={terpilih}
                  onChange={(o) => {
                    // Satu panggilan (bukan customHandler beruntun — yang kedua menimpa
                    // yang pertama). Jenis cuti bergantung Golongan karyawan, dan
                    // karyawan yang cuti tak boleh jadi penggantinya sendiri.
                    multiFieldHandler({
                      Nik: o.value,
                      JenisCuti: -1,
                      Pengganti: values.Pengganti === o.value ? "" : values.Pengganti,
                    });
                    setJumlahHari(0);
                    setSaldoKurang(true);
                    setSaldoTahun(null);
                  }}
                  options={karyawanList}
                  placeholder="Pilih karyawan…"
                  noOptionsMessage={() => "Tidak ada karyawan di Bagian Anda"}
                />
                {errTampil("Nik") && <p className="text-red-500 text-xs italic">{errors.Nik}</p>}
              </div>
              <div className="w-full md:w-1/2 px-3">
                <label className={LABEL}>Saldo Cuti{saldoTahun ? ` (${saldoTahun.periode || `Tahun ${saldoTahun.tahun}`})` : ""}</label>
                <input value={saldoTahun ? saldoTahun.saldo : terpilih ? terpilih.SisahCuti : ""} disabled className={READONLY} />
              </div>
            </div>
            {terpilih && (
              <div className="flex flex-wrap -mx-3 mb-6">
                <div className="w-full md:w-1/2 px-3 mb-6 md:mb-0">
                  <label className={LABEL}>Jabatan</label>
                  <input value={terpilih.NamaJabatan} disabled className={READONLY} />
                </div>
                <div className="w-full md:w-1/2 px-3">
                  <label className={LABEL}>Tipe Karyawan</label>
                  <input value={terpilih.NamaTipekaryawan} disabled className={READONLY} />
                </div>
              </div>
            )}

            {formLain.slice(0, 2).map(({ type, name, title, handler }) => (
              <HrdInputGenerator key={name} type={type} value={values} name={name} title={title}
                onChange={handler} touched={touched} buttonTouched={buttonTouched} errors={errors}
                modifiers="" size="full" required />
            ))}

            {/* Pengganti — juga dibatasi Bagian user, tanpa karyawan yang cuti */}
            <div className="flex flex-wrap -mx-3 mb-6">
              <div className="w-full px-3">
                <label className={LABEL}>Pengganti <span className="text-red-500">*</span></label>
                <Select
                  menuPortalTarget={document.body}
                  styles={SELECT_DI_MODAL}
                  value={opsiPengganti.find((k) => k.value === values.Pengganti) || null}
                  onChange={(o) => customHandler(o.value, "Pengganti")}
                  options={opsiPengganti}
                  placeholder="Pilih pengganti…"
                  noOptionsMessage={() => "Tidak ada karyawan lain di Bagian Anda"}
                />
                {errTampil("Pengganti") && <p className="text-red-500 text-xs italic">{errors.Pengganti}</p>}
              </div>
            </div>

            {formLain.slice(2).map(({ type, name, title, handler }) => (
              <HrdInputGenerator key={name} type={type} value={values} name={name} title={title}
                onChange={handler} touched={touched} buttonTouched={buttonTouched} errors={errors}
                modifiers="" size="full" required />
            ))}

            <div className="w-full">
              <label className={LABEL}>Jumlah Hari Terpakai</label>
              {mutationHari.isLoading ? (
                <span className="text-sm text-gray-400">Loading...</span>
              ) : (
                <input value={jumlahHari} disabled type="text"
                  className={saldoKurang && values.JenisCuti !== -1
                    ? "appearance-none block w-full text-gray-700 border border-red-500 rounded py-3 px-4 mb-3 leading-tight outline-none bg-white"
                    : "appearance-none block w-full text-green-700 border border-green-700 rounded py-3 px-4 leading-tight outline-none bg-white"} />
              )}
              {saldoKurang && values.Nik && values.JenisCuti !== -1 && !saldoTahun?.blokir && (
                <p className="text-red-500 text-xs italic">Saldo Tidak Cukup</p>
              )}
              {saldoTahun?.peringatan && values.Nik && (
                <p className="text-yellow-700 text-xs mt-1">{saldoTahun.peringatan}</p>
              )}
              {saldoTahun?.blokir && values.Nik && (
                <p className="text-red-500 text-xs italic">{saldoTahun.blokir}</p>
              )}
            </div>
          </form>
        </ModalBody>
        <ModalFooter>
          <Button color="red" buttonType="link" onClick={tutupForm} ripple="dark">Cancel</Button>
          <Button color={bisaSimpan ? "green" : "blueGray"} onClick={simpan}
            disabled={saldoKurang || mutationInput.isLoading} ripple={isValid ? "light" : ""}>
            {mutationInput.isLoading ? "Loading..." : "Submit"}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default EntryCutiPage;
